// oxlint-disable typescript/no-explicit-any
// `structuralSharing` and the `select` bound are TanStack Query's own signatures, and the
// conditional return type is only reachable through an assertion — both come from upstream.
// oxlint-disable eslint-js/no-restricted-syntax
// Copyright 2021-2023 The Connect Authors
//
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
//      http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.

import {
  create,
  type DescMessage,
  type DescMethodUnary,
  equals,
  isMessage,
  type MessageInitShape,
  type MessageShape,
} from '@bufbuild/protobuf';
import { type ContextValues, createContextValues, type Transport } from '@connectrpc/connect';
import {
  type ConnectQueryKey,
  // oxlint-disable-next-line no-restricted-imports
  createConnectQueryKey,
} from '@connectrpc/connect-query';
import { type QueryFunctionContext, type QueryKey, replaceEqualDeep, type QueryFunction } from '@tanstack/react-query';
import { callUnaryMethod } from './call-unary-method';
import { ctxKeyQueryClient } from './context-keys';

// Return type that conditionally includes select based on input options
type QueryOptionsResult<O extends DescMessage, TOptions extends { select?: (data: any) => any }> = {
  queryKey: ConnectQueryKey<O>;
  queryFn: QueryFunction<MessageShape<O>, ConnectQueryKey<O>>;
  structuralSharing: (oldData: unknown, newData: unknown) => unknown;
} & (TOptions extends { select: (data: MessageShape<O>) => infer R }
  ? { select: (data: MessageShape<O>) => R }
  : unknown);

/**
 * Creates the most appropriate options required to make a query.
 */
export function queryOptions<
  I extends DescMessage,
  O extends DescMessage,
  TOptions extends {
    transport: Transport;
    headers?: HeadersInit;
    contextValues?: ContextValues;
    // Per-call deadline override; falls back to the transport's `defaultTimeoutMs` when unset.
    timeoutMs?: number;
    select?: (data: MessageShape<O>) => unknown;
  },
>(
  schema: DescMethodUnary<I, O>,
  input: MessageInitShape<I> | undefined,
  { transport, headers, contextValues, timeoutMs, select }: TOptions,
): QueryOptionsResult<O, TOptions> {
  const queryKey = createConnectQueryKey({
    schema,
    input: input ?? create(schema.input),
    transport,
    headers,
    cardinality: 'finite',
  });
  const structuralSharing = createStructuralSharing(schema.output);
  const queryFn = createUnaryQueryFn(transport, schema, input, contextValues, timeoutMs);

  return {
    queryKey,
    queryFn,
    structuralSharing,
    select,
  } as unknown as QueryOptionsResult<O, TOptions>;
}

/**
 * Creates a unary query function.
 */
function createUnaryQueryFn<
  I extends DescMessage,
  O extends DescMessage,
  TQueryKey extends QueryKey,
  TPageParam = never,
>(
  transport: Transport,
  schema: DescMethodUnary<I, O>,
  input: MessageInitShape<I> | undefined,
  contextValues?: ContextValues,
  timeoutMs?: number,
) {
  return (context: QueryFunctionContext<TQueryKey, TPageParam>) =>
    callUnaryMethod(transport, schema, input, {
      signal: context?.signal,
      headers: (context.queryKey as unknown as ConnectQueryKey)[1].headers,
      timeoutMs,
      // Set query client in context to make available in interceptors
      contextValues: (contextValues ?? createContextValues()).set(ctxKeyQueryClient, context.client),
    });
}

/**
 * Returns a simplistic implementation for "structural sharing" for a Protobuf
 * message.
 *
 * To keep references intact between re-renders, we return the old version if it
 * equals the new version.
 *
 * @see https://tanstack.com/query/latest/docs/framework/react/guides/render-optimizations#structural-sharing
 */
function createStructuralSharing(schema: DescMessage) {
  return function structuralSharing(oldData: unknown, newData: unknown): unknown {
    if (!isMessage(oldData) || !isMessage(newData)) {
      return replaceEqualDeep(oldData, newData);
    }
    if (!equals(schema, oldData, newData)) {
      return newData;
    }
    return oldData;
  };
}
